import {
  CLIENT_PATH_TOKEN,
  COMPETITOR_PATH_TOKEN,
  ClientPathSchema,
  CompetitorsPathSchema,
  DEFAULT_CLIENT_PATH,
  DEFAULT_COMPETITORS_PATH,
  resolveCreativePath,
} from "@nexus/contracts";

export { DEFAULT_CLIENT_PATH, DEFAULT_COMPETITORS_PATH };

/** Previous name for the client default. Creative URLs read `clientPath`. */
export const DEFAULT_ASSETS_BASE_URL = DEFAULT_CLIENT_PATH;

/** Shipping competitor prefix. */
export const GCS_PUB_DATA_BASE = DEFAULT_COMPETITORS_PATH;

export function canonicalClientPath(value: unknown): string | null {
  const parsed = ClientPathSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function canonicalCompetitorsPath(value: unknown): string | null {
  const parsed = CompetitorsPathSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Folder for one client. A `{client}` token is replaced; otherwise the name is appended. */
export function clientCreativeFolder(clientPath: string, client: string): string {
  return resolveCreativePath(clientPath, CLIENT_PATH_TOKEN, client);
}

/** Folder for one competitor. A `{competitor}` token is replaced; otherwise the name is appended. */
export function competitorCreativeFolder(competitorsPath: string, competitor: string): string {
  return resolveCreativePath(competitorsPath, COMPETITOR_PATH_TOKEN, competitor);
}

export interface CreativeAssetUrls {
  thumbnailUrl: string;
  assetUrl: string;
}

/** First client partition when customerPartition is comma-separated (Retool parity). */
export function primaryClientPartition(customerPartition: string | null | undefined): string {
  if (!customerPartition) return "";
  return customerPartition.split(",")[0]?.trim() ?? "";
}

function assetExtension(objectType: string): "png" | "mp4" {
  return objectType.toLowerCase() === "image" ? "png" : "mp4";
}

/** Client creative URLs from `clientPath`. `{client}` is replaced, or the client name is appended, then `/thumbnails` or `/assets/{hash}`. */
export function buildCreativeAssetUrls(
  hash: string,
  objectType: string,
  customerPartition: string | null | undefined,
  clientPath: string = DEFAULT_CLIENT_PATH,
): CreativeAssetUrls {
  const folder = clientCreativeFolder(clientPath, primaryClientPartition(customerPartition));
  const ext = assetExtension(objectType);
  return {
    thumbnailUrl: `${folder}/thumbnails/${hash}.png`,
    assetUrl: `${folder}/assets/${hash}.${ext}`,
  };
}
